import { Controller } from "@hotwired/stimulus"
import { translations, fallbackLanguage } from "../i18n/translations"

const STORAGE_KEY = "planup-language"
const COOKIE_KEY = "planup-language"
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export default class extends Controller {
  static values = {
    language: { type: String, default: "nl" }
  }

  connect() {
    this.loadLanguage()
    this.translatePage()

    // Store bound function reference for proper cleanup
    this.boundHandleLanguageChange = this.handleLanguageChange.bind(this)

    // Listen for language change events from header controller
    document.addEventListener("languageChange", this.boundHandleLanguageChange)
  }

  disconnect() {
    document.removeEventListener("languageChange", this.boundHandleLanguageChange)
  }

  loadLanguage() {
    // Try to get language from localStorage
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && (stored === "nl" || stored === "en")) {
      this.languageValue = stored
      return
    }

    // Try to get language from browser
    const browserLang = navigator.language?.toLowerCase()
    if (browserLang) {
      if (browserLang.startsWith("nl")) {
        this.languageValue = "nl"
      } else if (browserLang.startsWith("en")) {
        this.languageValue = "en"
      }
    }

    // Persist the detected language
    this.persistLanguage(this.languageValue)
  }

  persistLanguage(lang) {
    localStorage.setItem(STORAGE_KEY, lang)
    document.cookie = `${COOKIE_KEY}=${lang}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
    document.documentElement.lang = lang
  }

  handleLanguageChange(event) {
    const newLang = event.detail?.language
    if (newLang && (newLang === "nl" || newLang === "en") && newLang !== this.languageValue) {
      this.languageValue = newLang
      this.persistLanguage(newLang)
      this.translatePage()
    }
  }

  t(key) {
    return translations[this.languageValue]?.[key] || translations[fallbackLanguage]?.[key] || key
  }

  translatePage() {
    // Translate all elements with data-i18n attribute
    const elements = document.querySelectorAll("[data-i18n]")
    elements.forEach(el => {
      const key = el.dataset.i18n
      if (key) {
        el.textContent = this.t(key)
      }
    })

    // Translate elements with data-i18n-html attribute (for HTML content)
    const htmlElements = document.querySelectorAll("[data-i18n-html]")
    htmlElements.forEach(el => {
      const key = el.dataset.i18nHtml
      if (key) {
        el.innerHTML = this.t(key)
      }
    })

    // Translate placeholder attributes
    const placeholderElements = document.querySelectorAll("[data-i18n-placeholder]")
    placeholderElements.forEach(el => {
      const key = el.dataset.i18nPlaceholder
      if (key) {
        el.placeholder = this.t(key)
      }
    })

    // Translate aria-label attributes
    const ariaElements = document.querySelectorAll("[data-i18n-aria]")
    ariaElements.forEach(el => {
      const key = el.dataset.i18nAria
      if (key) {
        el.setAttribute("aria-label", this.t(key))
      }
    })

    // Update the document language
    document.documentElement.lang = this.languageValue
  }

  // Expose current language for other controllers
  get currentLanguage() {
    return this.languageValue
  }
}
